'use strict';

const _ = require('underscore');
const d3 = require('d3');
const LinkView = require('common/ui/view/svg/link');
const IconView = require('common/ui/view/svg/icon');
const TextlabelView = require('common/ui/view/svg/text/label');
const Util = require('common/ui/view/svg/util');
const ElementAction = require('system/domain/action/element');
const RelationTypeRepository = require('system/domain/repository/relation-type');
const ProjectRepository = require('common/domain/repository/project');
const SyncStatesModel = require('system/domain/entity/sync-states');
const CommentCounterView = require('system/ui/view/element-communication/comment-counter-view');
const TaskCounterView = require('system/ui/view/element-communication/task-counter-view');

const RelationView = LinkView.extend({
    className: 'link',
    marks: function() {
        return this.getType().get('marks');
    },
    syncState: function() {
        return new SyncStatesModel({models: [
            this.model.syncState,
            this.meta.syncState
        ]})
    },
    label: function() {
        const name = this.model.get('name');
        if(name) {
            return name;
        }
        return this.getType().get('name');
    },
    bendAt: function() {
        return this.meta.get('vendAt');
    },
    lineType: function() {
        const type = this.getType().get('lineType')
        if(type) {
            return type;
        } else {
            return 'solid';
        }
    },
    getType: function() {
        const type = this.model.get('type');
        return RelationTypeRepository.getRelationTypes().get(type);
    },

    initialize: function(params) {
        this.meta = params.meta;
        this._context = this.getOption('context');
        this._channel = this.getOption('channel');
        this._auth = this.getOption('auth');

        this.constructor.__super__.initialize.call(this, params);
        this.listenTo(this.model, 'select', this.select);
        this.listenTo(this.model, 'deselect', this.deselect);
        this.listenTo(this.model, 'change:name', this.render);
        this.listenTo(this.model, 'change:type', this.render);
        this.listenTo(this.model, 'change:deleting', this.render);
        this.listenTo(this.model, 'change:multiplicity', this.render);
        this.listenTo(this.model, 'change:deleted', this.remove);
        this.listenTo(this.model, 'highlight', this.highlight);
        this.listenTo(this.model, 'dehighlight', this.dehighlight);
        this.listenTo(this.meta, 'change', this.move);
        this.listenTo(this.meta, 'change:deleting', this.render);
        this.listenTo(this.meta, 'change:deleted', this.remove);
        this.listenTo(this.meta, 'change:vendAt', this.onMove);
        this.listenTo(this.meta, 'remove', this.remove);
    },

    onRender: function() {
        this.constructor.__super__.onRender.call(this);
        if(this.model.selected) {
            this.select();
        }
        this.renderDiffState();
        this.renderIcon();
        this.renderCommunicationCounters();
        this.renderMultiplicity();
        if(this._auth.isLinkVendable() && this.model.selected) {
            this.renderBender();
        } else {
            this.removeBender();
        }
        if(this.model.get('deleting') || this.meta.get('deleting')) {
            this.$el.style('opacity', '0.5');
        } else {
            this.$el.style('opacity', null);
        }
    },

    onClick: function() {
        this.model.click();
    },

    onBend: function(bendAt) {
        ElementAction.startEditLink({
            context: this._context,
            link: {id: this.model.id}
        });
        ElementAction.updateLink({
            context: this._context,
            linkMeta: {
                id: this.model.id,
                vendAt: bendAt
            }
        });
    },

    onMove: function() {
        if(this._auth.isMultiSelectable()) {
            this.removeLabel();
            this.removeBender();
            this.removeMark();
            this.removeSyncState();
            this.removeIcon();
            this.calcPos();
            this.renderLine();
            return;
        }

        RelationView.__super__.onMove.call(this);
    },

    onMoveEnd: function() {
        if(this._auth.isMultiSelectable()) {
            this.calcPos();
            this.render();
        }
    },

    select: function() {
        this.$el.classed('is-active', true);
        if(this._auth.isLinkVendable()) {
            this.renderBender();
        }
    },

    deselect: function() {
        this.$el.classed('is-active', false);
        this.removeBender();
    },

    highlight: function() {
        this.$el.classed('is-highlighted', true);
    },

    dehighlight: function() {
        this.$el.classed('is-highlighted', false);
    },

    renderDiffState: function() {
        const diff = this.model.diff;
        const pos = this.line.getDividingPoint(0.4);
        this.$el.selectAll('.link-diff').remove();
        if(!diff || !diff.changed) {
            return
        }

        let klass = '';
        if(!diff.to){
            klass = 'link-diff-delete';
        } else if (!diff.from) {
            klass = 'link-diff-add';
        }
        const text = diff.diffId;

        const diffView = this.$el.append('g')
            .classed('link-diff', true);
        diffView.append('circle')
            .attr('r', 40)
            .attr('cx', 0)
            .attr('cy', 0)
            .classed(klass, true);

        const icon = new IconView({
            text: text,
            offset: {x: 0, y: -4},
            size: 64,
            fontSize: 32
        });
        diffView.append(icon.render().el);
        diffView.attr('transform', Util.transform(pos));
    },

    renderIcon: function() {
        const className = this.getType().get('faicon');
        if (!className) {
            return;
        }
        this.removeIcon();

        const iconView = this.$el.append(
            (new IconView({
                class: className,
                offset: {x: 0, y: -60},
                size: 50
            })).render().el);
        iconView
            .classed('link-icon', true)
            .attr('transform', Util.transform(
                this.line.getDividingPoint(0.5)
            ));
    },

    renderCommunicationCounters: function() {
        // 新規作成されたばかり(保存前)の場合は処理スキップ
        if (!this.model.key.kind) {
            return;
        }

        const params = {
            width: 128,
            height: 64,
            fontSize: 64,
            badgeFontSize: 32,
        };

        const commentsCounterPos = this.line.getDividingPoint(0.6);
        const tasksCounterPos = {
            x: commentsCounterPos.x + params.width + 10,
            y: commentsCounterPos.y,
        };

        const oldContext = this._context;
        ProjectRepository.getProject(oldContext.projectId)
            .then(project => {
                this._renderCommentsCounter(project, this.model.key, params, commentsCounterPos);
                this._renderTasksCounter(project, this.model.key, params, tasksCounterPos);
            });
    },

    _renderCommentsCounter: function(project, key, params, offset) {
        this.$el.selectAll('.element-comments-counter').remove();

        const commentCounterView = new CommentCounterView({
            project: project,
            key: key,
            hasComment: this.model.hasRelatedComment(),
            class: 'fa fa-comments-o',
            bgclass: 'element-communication-counter',
            width: params.width,
            height: params.height,
            fontSize: params.fontSize,
            offset: offset,
            badgeFontSize: params.badgeFontSize,
        });
        this.$el.append('g')
            .append(commentCounterView.render().el)
            .classed('element-comments-counter', true);
    },

    _renderTasksCounter: function(project, key, params, offset) {
        this.$el.selectAll('.element-tasks-counter').remove();

        const taskCounterView = new TaskCounterView({
            project: project,
            key: key,
            hasTask: this.model.hasRelatedTask(),
            class: 'fa fa-check-square',
            bgclass: 'element-communication-counter',
            width: params.width,
            height: params.height,
            fontSize: params.fontSize,
            offset: offset,
            badgeFontSize: params.badgeFontSize,
        });
        this.$el.append('g')
            .append(taskCounterView.render().el)
            .classed('element-tasks-counter', true);
    },

    removeIcon: function() {
        this.$el.selectAll('.link-icon').remove();
    },

    renderMultiplicity: function() {
        this.$el.selectAll('.link-multiplicity').remove();
        const m = this.model.get('multiplicity');
        if (!m) {
            return;
        }

        _.each({source: 0.05, target: 0.95}, function(p, key){
            const upper = m[key].upper;
            const lower = m[key].lower;

            let multi = '';
            if(upper == lower) {
                multi = upper;
            } else if(!upper) {
                multi = lower + '..*';
            } else {
                multi = lower + '..' + upper;
            }

            this.$el.append(
                (new TextlabelView({
                    text: multi,
                    offset: {x: 0, y: 0},
                    size: 32
                })).render().el)
                .classed('link-multiplicity', true)
                .attr('transform', Util.transform(
                    this.line.getDividingPoint(p)));
        }, this);
    }
});

module.exports = RelationView;



// WEBPACK FOOTER //
// ./system/ui/view/element/relation.js